import { create } from 'zustand';
import {
  ScheduleLine,
  ScheduleLinesState,
  SchedulePatternLine,
} from 'pages/Schedule/store/scheduleLines.types';
import { getRowId } from 'pages/Schedule/LineScheduleTable/LineScheduleTable.constants';
import {
  B2bCustomer,
  ByLinePattern,
  ByLinePatternsSchedule,
  Mutation_AddPatternToScheduleMutation,
  Mutation_AddPatternToScheduleMutationVariables,
  Mutation_AssignScheduleLineMutation,
  Mutation_AssignScheduleLineMutationVariables,
  Mutation_RemovePatternFromScheduleMutation,
  Mutation_RemovePatternFromScheduleMutationVariables,
  Mutation_RerankPatternsScheduleMutation,
  Mutation_RerankPatternsScheduleMutationVariables,
  ScheduleByLineSchedulesWithAppliedScheduleQuery,
  ScheduleByLineSchedulesWithAppliedScheduleQueryVariables,
  ScheduleCollection,
  ScheduleSingleLineQuery,
  ScheduleSingleLineQueryVariables,
  ScheduleTotalsAndConflictsQuery,
  ScheduleTotalsAndConflictsQueryVariables,
  SecondaryUser,
} from 'types/graphql';
import {
  MUTATION_addPatternToSchedule,
  MUTATION_assignScheduleLine,
  MUTATION_removePatternFromSchedule,
  MUTATION_rerankPatternsSchedule,
} from 'mutation';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import {
  QUERY_scheduleByLineSchedulesWithAppliedSchedule,
  QUERY_scheduleTotalsAndConflicts,
  QUERY_scheduleSingleLine,
} from 'queries';
import { devtoolsInNonProd } from 'store/zustand/index.utils';

const sortLinesByRank = (a: ScheduleLine, b: ScheduleLine) => a.rank - b.rank;

const updateLinesWithNewRowIndex = (lines: ScheduleLine[]) =>
  lines
    .sort(sortLinesByRank)
    .map((line, index) => ({ ...line, id: getRowId(index), rowIndex: index }));

const mapByLineSchedulesToViewSchedules = (scheduleCollection: ScheduleCollection) => ({
  totalHours: scheduleCollection.totalHours,
  totalHoursByDay: scheduleCollection.totalHoursByDay || [],
  conflicts: scheduleCollection.conflicts,
  warnings: scheduleCollection.warnings,
  positionShifts: scheduleCollection.positionShifts || [],
  totalPositionHours: scheduleCollection.totalPositionHours || undefined,
  filteredPositionHoursByDay: scheduleCollection.filteredPositionHoursByDay || [],
  lineSchedules:
    scheduleCollection.byLineSchedules?.map(
      ({ name, isOwner, relayId, displayId, appliedSchedule }) =>
        ({
          name,
          displayId: `${displayId}`,
          relayId,
          isOwner,
          totalHours: appliedSchedule?.totalHours,
          totalHoursByDay: appliedSchedule?.totalHoursByDay || [],
          filteredHours: appliedSchedule?.filteredHours,
          filteredHoursByDay: appliedSchedule?.filteredHoursByDay || [],
          lines:
            appliedSchedule?.patternsSchedules?.map((patternSchedule) => ({
              displayId: `${patternSchedule.displayId}`,
              relayId: patternSchedule.relayId,
              rank: patternSchedule.rank,
              pattern: patternSchedule.pattern,
              defaultPosition: patternSchedule.defaultPosition,
              schedulingLine: patternSchedule.schedulingLine,
              ghostShiftsLine: patternSchedule.ghostShiftsLine,
            })) || [],
        } as SchedulePatternLine),
    ) || [],
});

const toScheduleLine = ({
  rank,
  displayId,
  pattern,
  relayId,
  defaultPosition,
  schedulingLine,
  ghostShiftsLine,
}: Pick<
  ByLinePatternsSchedule,
  | 'rank'
  | 'displayId'
  | 'pattern'
  | 'relayId'
  | 'defaultPosition'
  | 'schedulingLine'
  | 'ghostShiftsLine'
>): ScheduleLine => ({
  rank,
  displayId: `${displayId}`,
  pattern: pattern as ByLinePattern,
  defaultPosition,
  schedulingLine,
  ghostShiftsLine,
  relayId,
});

const calculateLineRank = (lines: ScheduleLine[], rowIndex: number): number => {
  if (lines.length === 0 || rowIndex === 0) {
    return 1;
  }

  if (rowIndex >= lines.length) {
    return lines[lines.length - 1].rank + 1;
  }

  // if line exists at rowIndex
  const existingLine = lines.find((line) => line.rowIndex === rowIndex);
  if (existingLine) {
    return existingLine.rank;
  }

  // find highest rank
  const precedingLines = lines
    .filter((line) => line.rowIndex && line.rowIndex <= rowIndex)
    .sort((a, b) => b.rank - a.rank);

  return precedingLines.length > 0 ? precedingLines[0].rank + 1 : 1;
};

export const scheduleLinesStore = create<ScheduleLinesState>()(
  devtoolsInNonProd((set, get) => ({
    lines: [],
    schedules: {
      lineSchedules: [],
      totalHours: { rawTime: 0, rawTimeInSeconds: 0, time: 0 },
      totalPositionHours: { rawTime: 0, rawTimeInSeconds: 0, time: 0 },
      totalHoursByDay: [],
      filteredPositionHoursByDay: [],
      conflicts: undefined,
      warnings: undefined,
    },
    isReranking: false,
    isMultiSelectActive: false,
    actionLoading: false,
    filterLoading: 0,
    loadingTimeout: null,
    error: null,
    latestFilterRequestId: 0,
    latestFilterRequestVariablesSerialized: '',

    setIsMultiSelectActive: (active: boolean) => set({ isMultiSelectActive: active }),

    getIsReranking: () => get().isReranking,

    fetchData: async (apolloClient, variables, isFilterChange = false) => {
      let requestId: number | null = null;

      if (isFilterChange) {
        const newFilterRequestVariablesSerialized = JSON.stringify(variables);

        if (get().latestFilterRequestVariablesSerialized === newFilterRequestVariablesSerialized) {
          return;
        }

        // Generate and store the new request ID
        set((state) => ({
          latestFilterRequestVariablesSerialized: newFilterRequestVariablesSerialized,
          latestFilterRequestId: state.latestFilterRequestId + 1,
          filterLoading: state.filterLoading + 1,
          actionLoading: false,
          error: null,
        }));

        requestId = get().latestFilterRequestId;
      } else {
        set((state) => ({
          actionLoading: true,
          error: null,
        }));
      }

      try {
        const { data } = await apolloClient.query<
          ScheduleByLineSchedulesWithAppliedScheduleQuery,
          ScheduleByLineSchedulesWithAppliedScheduleQueryVariables
        >({
          query: QUERY_scheduleByLineSchedulesWithAppliedSchedule,
          variables,
          fetchPolicy: 'network-only',
        });

        if (isFilterChange && Number(requestId) < get().latestFilterRequestId) {
          set((state) => ({
            filterLoading: Math.max(0, state.filterLoading - 1),
          }));
          return;
        }

        const byLineScheduleCollection =
          (data?.me as B2bCustomer | SecondaryUser)?.byLineScheduleCollection || {};

        const schedules = mapByLineSchedulesToViewSchedules(byLineScheduleCollection);

        set((state) => {
          const newLines =
            byLineScheduleCollection?.byLineSchedules?.[0]?.appliedSchedule?.patternsSchedules?.map(
              toScheduleLine,
            ) || [];

          return {
            ...state,
            latestFilterRequestVariablesSerialized: '',
            lines: updateLinesWithNewRowIndex(newLines),
            schedules,
            actionLoading: false,
            filterLoading: isFilterChange
              ? Math.max(0, state.filterLoading - 1)
              : state.filterLoading,
          };
        });
      } catch (error) {
        if (isFilterChange && Number(requestId) < get().latestFilterRequestId) {
          set((state) => ({
            filterLoading: Math.max(0, state.filterLoading - 1),
          }));
          return;
        }

        handleGenericErrorMessage(error);
        set((state) => ({
          error,
          actionLoading: false,
          filterLoading: isFilterChange
            ? Math.max(0, state.filterLoading - 1)
            : state.filterLoading,
        }));
      }
    },

    // Actions
    addLine: async (apolloClient, { rowIndex, patternId }, scheduleId) => {
      const { lines } = get();

      set({ actionLoading: true, error: null });

      let lineRank = calculateLineRank(lines, rowIndex);

      try {
        await apolloClient.mutate<
          Mutation_AddPatternToScheduleMutation,
          Mutation_AddPatternToScheduleMutationVariables
        >({
          mutation: MUTATION_addPatternToSchedule,
          variables: {
            patternId: patternId,
            scheduleId: scheduleId,
            createPatternsScheduleParams: {
              rank: lineRank,
            },
          },
        });

        set({ actionLoading: false });
      } catch (error) {
        handleGenericErrorMessage(error);
        set({
          error,
          actionLoading: false,
        });
        handleGenericErrorMessage(error);
      }
    },

    deleteLine: async (apolloClient, rowIndex) => {
      set({ actionLoading: true, error: null });
      const { lines } = get();
      const relayId = lines.find((line) => line.rowIndex === rowIndex)?.relayId;

      try {
        await apolloClient.mutate<
          Mutation_RemovePatternFromScheduleMutation,
          Mutation_RemovePatternFromScheduleMutationVariables
        >({
          mutation: MUTATION_removePatternFromSchedule,
          variables: { patternsScheduleId: `${relayId}` },
        });
        const filteredLines = lines.filter((line) => line.rowIndex !== rowIndex);
        set((state) => ({
          ...state,
          actionLoading: false,
          lines: updateLinesWithNewRowIndex(filteredLines),
        }));
      } catch (error) {
        handleGenericErrorMessage(error);
        set({
          error,
          actionLoading: false,
        });
      }
    },

    assignLine: async (apolloClient, assignLineVariables) => {
      set({ actionLoading: true, error: null });
      try {
        await apolloClient.mutate<
          Mutation_AssignScheduleLineMutation,
          Mutation_AssignScheduleLineMutationVariables
        >({
          mutation: MUTATION_assignScheduleLine,
          variables: assignLineVariables,
        });
        set({
          actionLoading: false,
        });
      } catch (error) {
        handleGenericErrorMessage(error);
        set({
          error,
          actionLoading: false,
        });
      }
    },

    hasLineForRow: (rowIndex) => {
      const { lines } = get();
      return lines.some((line) => line.rowIndex === rowIndex);
    },

    getLineNumber: (selectedLine) => {
      const { lines } = get();
      const index = lines.findIndex((line) => line.rowIndex === selectedLine.rowIndex);
      return index !== -1 ? index + 1 : 0;
    },

    getLineForRow: (rowIndex) => {
      const { lines } = get();
      return lines.find((line) => line.rowIndex === rowIndex);
    },

    updateLineByRowIndex: async (
      apolloClient,
      { currentRowIndex, newRowIndex },
      { patternsScheduleId },
      lineDateRange,
    ) => {
      const { lines } = get();
      set({ isReranking: true });

      if (currentRowIndex === newRowIndex) {
        set({ isReranking: false });
        return;
      }

      const lineBeingMoved = lines.find((line) => line.rowIndex === currentRowIndex);
      if (!lineBeingMoved) {
        set({ isReranking: false });
        return;
      }

      let updatedLines = [...lines];

      // Remove the line being moved from the array
      updatedLines = updatedLines.filter((line) => line.rowIndex !== currentRowIndex);

      const newRank = calculateLineRank(lines, newRowIndex);

      // - 0.5 will force the line to be temporarily placed in the drop row
      // temporary operation until mutation is complete and data is refreshed
      // only do this when dragging from higher index to lower index
      updatedLines.push({
        ...lineBeingMoved,
        rank: currentRowIndex > newRowIndex ? newRank - 0.5 : newRank,
      });

      set((state) => ({
        ...state,
        lines: updateLinesWithNewRowIndex(updatedLines),
      }));

      try {
        const result = await apolloClient.mutate<
          Mutation_RerankPatternsScheduleMutation,
          Mutation_RerankPatternsScheduleMutationVariables
        >({
          mutation: MUTATION_rerankPatternsSchedule,
          variables: {
            lineDateRange,
            input: {
              newRank,
              patternsScheduleId: patternsScheduleId,
            },
          },
        });
        const patternSchedules =
          result.data?.rerankPatternsSchedule?.schedule?.appliedSchedule?.patternsSchedules;
        if (patternSchedules) {
          const newLines =
            patternSchedules.map((patternSchedule) =>
              toScheduleLine(patternSchedule as ByLinePatternsSchedule),
            ) || [];

          set((state) => ({
            ...state,
            lines: updateLinesWithNewRowIndex(newLines),
          }));
        } else {
          handleGenericErrorMessage('Schedule is empty');
        }
      } catch (error) {
        handleGenericErrorMessage(error);
        set({
          error,
        });
      } finally {
        set({ isReranking: false });
      }
    },

    fetchTotalsAndConflicts: async (apolloClient, variables) => {
      set({ actionLoading: true });
      try {
        const { data } = await apolloClient.query<
          ScheduleTotalsAndConflictsQuery,
          ScheduleTotalsAndConflictsQueryVariables
        >({
          query: QUERY_scheduleTotalsAndConflicts,
          variables,
          fetchPolicy: 'network-only',
        });

        const byLineScheduleCollection =
          (data?.me as B2bCustomer | SecondaryUser)?.byLineScheduleCollection ||
          ({} as ScheduleCollection);

        set((state) => {
          const updatedLineSchedules = state.schedules.lineSchedules.map((schedule) => {
            const updatedSchedule = byLineScheduleCollection?.byLineSchedules?.find(
              (s) => s.relayId === schedule.relayId,
            );
            if (!updatedSchedule?.appliedSchedule) return schedule;
            return {
              ...schedule,
              totalHours: updatedSchedule.appliedSchedule.totalHours,
              totalHoursByDay: updatedSchedule.appliedSchedule.totalHoursByDay || [],
              filteredHours: updatedSchedule.appliedSchedule.filteredHours,
              filteredHoursByDay: updatedSchedule.appliedSchedule.filteredHoursByDay || [],
            };
          });

          return {
            ...state,
            schedules: {
              ...state.schedules,
              totalHours: byLineScheduleCollection.totalHours,
              totalHoursByDay: byLineScheduleCollection.totalHoursByDay || [],
              conflicts: byLineScheduleCollection.conflicts,
              warnings: byLineScheduleCollection.warnings,
              positionShifts: byLineScheduleCollection.positionShifts || [],
              totalPositionHours: byLineScheduleCollection.totalPositionHours || undefined,
              filteredPositionHoursByDay: byLineScheduleCollection.filteredPositionHoursByDay || [],
              lineSchedules: updatedLineSchedules,
            },
            actionLoading: false,
          };
        });
      } catch (error) {
        handleGenericErrorMessage(error);
        set({ error, actionLoading: false });
      }
    },

    fetchSingleLine: async (apolloClient, variables, targetRelayId) => {
      set({ actionLoading: true });
      try {
        const { data } = await apolloClient.query<
          ScheduleSingleLineQuery,
          ScheduleSingleLineQueryVariables
        >({
          query: QUERY_scheduleSingleLine,
          variables,
          fetchPolicy: 'network-only',
        });

        const byLineScheduleCollection =
          (data?.me as B2bCustomer | SecondaryUser)?.byLineScheduleCollection ||
          ({} as ScheduleCollection);

        let updatedPatternSchedule: ByLinePatternsSchedule | undefined;

        byLineScheduleCollection?.byLineSchedules?.some((s) => {
          const found = s.appliedSchedule?.patternsSchedules?.find(
            (ps) => ps.relayId === targetRelayId || String(ps.displayId) === targetRelayId,
          );
          if (found) {
            updatedPatternSchedule = found;
            return true;
          }
          return false;
        });

        if (!updatedPatternSchedule) {
          set({ actionLoading: false });
          return;
        }

        const updatedLine = toScheduleLine(updatedPatternSchedule);

        set((state) => {
          const newLines = state.lines.map((line) =>
            line.relayId === updatedLine.relayId ||
            (line.displayId && line.displayId === updatedLine.displayId)
              ? { ...updatedLine, rowIndex: line.rowIndex }
              : line,
          );

          const updatedLineSchedules = state.schedules.lineSchedules.map((schedule) => ({
            ...schedule,
            lines: schedule.lines.map((line) =>
              line.relayId === updatedLine.relayId ||
              (line.displayId && line.displayId === updatedLine.displayId)
                ? { ...updatedLine, rowIndex: line.rowIndex }
                : line,
            ),
          }));

          return {
            ...state,
            lines: newLines,
            schedules: {
              ...state.schedules,
              lineSchedules: updatedLineSchedules,
            },
            actionLoading: false,
          };
        });
      } catch (error) {
        handleGenericErrorMessage(error);
        set({ error, actionLoading: false });
      }
    },

    reset: () =>
      set({
        lines: [],
        schedules: {
          lineSchedules: [],
          totalHours: { rawTime: 0, rawTimeInSeconds: 0, time: 0 },
          totalHoursByDay: [],
          filteredPositionHoursByDay: [],
          conflicts: undefined,
          warnings: undefined,
        },
        isMultiSelectActive: false,
        actionLoading: false,
        filterLoading: 0,
        error: null,
      }),
  })),
);

export const useScheduleLinesStore = scheduleLinesStore;
